import React, { useRef, useCallback, useEffect } from "react";
import { Box } from "@chakra-ui/react";

interface JinjaHighlightedTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isReadOnly?: boolean;
}

// These are the computed style properties that affect text layout.
// We copy them from the textarea to the pre after mount so both elements
// are guaranteed to have identical text rendering — regardless of browser
// defaults, Chakra resets, or inherited styles.
const MIRRORED_STYLES: string[] = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontVariant",
  "fontSizeAdjust",
  "fontStretch",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textIndent",
  "textRendering",
  "textTransform",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "boxSizing",
  "tabSize",
  "whiteSpace",
  "wordBreak",
  "overflowWrap",
  "width",
];

const highlightJinja = (text: string): string => {
  if (!text) return "";

  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped
    .replace(
      /(\{\{[\s\S]*?\}\})/g,
      "<span style='display:inline;color:rgb(158,18,8);'>$1</span>"
    )
    .replace(
      /(\{%[\s\S]*?%\})/g,
      "<span style='display:inline;color:rgb(28,75,177);'>$1</span>"
    )
    .replace(
      /(\{#[\s\S]*?#\})/g,
      "<span style='display:inline;color:rgb(19,128,59);'>$1</span>"
    );
};

const JinjaHighlightedTextarea: React.FC<JinjaHighlightedTextareaProps> = ({
  value,
  onChange,
  placeholder,
  isReadOnly = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const preRef = useRef<HTMLPreElement>(null);

  // Copy computed styles from textarea to pre so text layout is pixel-identical.
  // Called once after mount and again on any resize (font loading, zoom, etc.)
  const mirrorStyles = useCallback(() => {
    const ta = textareaRef.current;
    const pre = preRef.current;
    if (!ta || !pre) return;

    const computed = window.getComputedStyle(ta);
    MIRRORED_STYLES.forEach((prop) => {
      try {
        (pre.style as any)[prop] = (computed as any)[prop];
      } catch {
        // ignore read-only props
      }
    });

    // Keep border transparent but same width so box model matches exactly
    pre.style.borderColor = "transparent";
    pre.style.borderStyle = computed.borderStyle;
  }, []);

  useEffect(() => {
    mirrorStyles();

    const ta = textareaRef.current;
    if (!ta) return;
    const ro = new ResizeObserver(mirrorStyles);
    ro.observe(ta);
    return () => ro.disconnect();
  }, [mirrorStyles]);

  const syncScroll = useCallback(() => {
    const ta = textareaRef.current;
    const pre = preRef.current;
    if (ta && pre) {
      pre.scrollTop = ta.scrollTop;
      pre.scrollLeft = ta.scrollLeft;
    }
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") syncScroll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [syncScroll]);

  // Always append trailing newline — without it the <pre> is one line shorter
  // than the textarea, pushing highlights one line above the actual cursor.
  const highlightedHTML = value
    ? highlightJinja(value) + "\n"
    : `<span style="color:#a0aec0;">${placeholder ?? ""}</span>\n`;

  return (
    <Box position="relative" width="100%" height="100%">
      {/* Highlight layer — styles mirrored from textarea after mount */}
      <pre
        ref={preRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 0,
          overflow: "hidden",
          pointerEvents: "none",
          textAlign: "left",
          color: "#2D3748",
          backgroundColor: isReadOnly ? "#F7FAFC" : "transparent",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
        dangerouslySetInnerHTML={{ __html: highlightedHTML }}
      />

      {/* Textarea — transparent text so only caret is visible */}
      <textarea
        ref={textareaRef}
        className="jinja-ta"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={syncScroll}
        onFocus={syncScroll}
        readOnly={isReadOnly}
        placeholder={placeholder}
        spellCheck={false}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          padding: "8px 12px",
          fontFamily: "'Monaco', 'Menlo', 'Ubuntu Mono', monospace",
          fontSize: "13px",
          lineHeight: "21px",
          resize: "none",
          border: "1px solid #CBD5E0",
          borderRadius: "6px",
          background: "transparent",
          color: "transparent",
          caretColor: "#1A202C",
          outline: "none",
          textAlign: "left",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          boxSizing: "border-box",
          cursor: isReadOnly ? "not-allowed" : "text",
        }}
      />

      <style>{`
        textarea.jinja-ta::placeholder { color: transparent; }
        textarea.jinja-ta::-webkit-scrollbar { width: 4px; }
        textarea.jinja-ta::-webkit-scrollbar-track { background: transparent; }
        textarea.jinja-ta::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 2px; }
        textarea.jinja-ta:hover { border-color: #63B3ED !important; }
        textarea.jinja-ta:focus { border-color: #4299E1 !important; }
      `}</style>
    </Box>
  );
};

export default JinjaHighlightedTextarea;